import { CurrencyPipe, DatePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, QueryList, ViewChildren, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { PublicReportResponse, ReportApplication } from '@app/models/reports';
import { QbService, ReportService, ToastService } from '@app/services';
import { NgxMaskPipe } from 'ngx-mask';
import { filter, finalize, switchMap, tap } from 'rxjs';

@Component({
  selector: 'qb-public-report',
  imports: [
    CurrencyPipe,
    DatePipe,
    MatTable,
    MatSort,
    MatSortHeader,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    MatPaginator,
    NgxMaskPipe,
    RouterLink,
  ],
  templateUrl: './public-report.component.html',
  styleUrl: './public-report.component.scss',
})
export class PublicReportComponent implements OnInit {
  @ViewChildren(MatSort) reportSort: QueryList<MatSort>;
  @ViewChildren('paginator') paginators: QueryList<MatPaginator>;

  private destroyRef = inject(DestroyRef);

  agencyPrefix: string;
  agencyName: string;
  reportName: string;
  reportTitle: string;
  questionnaireTypeId = 1;
  loadedAt = new Date();
  loading = false;
  /** True when the URL ends with /pdf or /excel (download) instead of showing the table. */
  fileRequest = false;
  downloadFormat: 'pdf' | 'excel' | null = null;
  downloading = false;
  downloadError = false;

  reportDataSources: MatTableDataSource<ReportApplication>[] = [];
  columnsToDisplay: string[] = [];
  pageSize = 25;
  pageSizeOptions: number[] = [10, 25, 100];

  constructor(
    private route: ActivatedRoute,
    private reportService: ReportService,
    private toast: ToastService,
    public qb: QbService
  ) {}

  ngOnInit(): void {
    this.route.params
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(params => {
          this.agencyPrefix = params['agencyPrefix'];
          this.reportName = params['reportName'];
          this.fileRequest = params['format'] != null;
          const format = params['format'];
          this.downloadFormat = format === 'pdf' || format === 'excel' ? format : null;
          this.questionnaireTypeId = this.reportName === 'cupccaa-approved' ? 2 : 1;
          this.reportTitle = this.getReportTitle(this.reportName);
          this.columnsToDisplay = this.getColumnsToDisplay(this.questionnaireTypeId);
          this.downloadError =
            this.fileRequest &&
            (!this.downloadFormat ||
              (this.reportName !== 'prequal-approved' && this.reportName !== 'cupccaa-approved'));

          if (this.fileRequest) {
            this.loading = false;
            this.downloading = !this.downloadError;
            this.qb.loading = !this.downloadError;
          } else {
            this.loading = true;
            this.downloading = false;
            this.qb.loading = true;
          }
          this.reportDataSources = [];
        }),
        // Bad /pdf or /excel URLs: show the error state above and do not call the API.
        filter(() => !this.fileRequest || !this.downloadError),
        switchMap(() => {
          if (this.fileRequest) {
            return this.reportService
              .downloadPublicReport(this.questionnaireTypeId, this.agencyPrefix, this.downloadFormat!)
              .pipe(
                finalize(() => {
                  this.downloading = false;
                  this.qb.loading = false;
                })
              );
          }
          return this.reportService.runPublicReport(this.questionnaireTypeId, this.agencyPrefix).pipe(
            finalize(() => {
              this.loading = false;
            })
          );
        })
      )
      .subscribe({
        next: (report: PublicReportResponse) => {
          if (this.fileRequest || !report?.vendors) {
            return;
          }
          this.loadedAt = new Date();
          this.agencyName = report.agencyName;
          report.vendors.forEach(group =>
            group.forEach(app => {
              app.licenseString = app.licenseClassifications?.map(val => val.code).join(', ') ?? '';
            })
          );
          this.setData(report.vendors);
        },
        error: () => {
          this.qb.loading = false;
          this.loading = false;
          this.downloading = false;
          if (this.fileRequest) {
            this.downloadError = true;
            this.toast.error('Error downloading public report');
            return;
          }
          this.toast.error('Error loading public report');
        },
      });
  }

  private getReportTitle(reportName: string): string {
    switch (reportName) {
      case 'cupccaa-approved':
        return 'CUPCCAA Contractor List';
      case 'prequal-approved':
      default:
        return 'Prequalification Approved Contractor List';
    }
  }

  private getColumnsToDisplay(questionnaireTypeId: number): string[] {
    const columns = [
      'company',
      'address',
      'contractor',
      'phoneNumber',
      'emailAddress',
      'licenseString',
      'expirationDate',
    ];
    if (questionnaireTypeId === 1) {
      columns.splice(columns.indexOf('expirationDate'), 0, 'rateFinalization');
    }
    return columns;
  }

  private setData(appGroups: ReportApplication[][]): void {
    this.reportDataSources = [];
    appGroups.forEach((group, i) => {
      const dataSource = new MatTableDataSource<ReportApplication>(group);
      this.configureSorting(dataSource);
      this.reportDataSources.push(dataSource);

      setTimeout(() => {
        dataSource.paginator = this.paginators.get(i);
        if (this.reportSort) {
          const sorts = this.reportSort.toArray();
          dataSource.sort = sorts[i];
          sorts[i]?.sort({ id: 'company', start: 'asc', disableClear: true });
        }
        this.qb.loading = false;
      });
    });

    if (!appGroups.length) {
      this.qb.loading = false;
    }
  }

  private configureSorting(ds: MatTableDataSource<ReportApplication>) {
    const dateFields = new Set(['lastReviewedDate', 'expirationDate']);
    ds.sortingDataAccessor = (item: ReportApplication, property: string) => {
      if (property === 'address') {
        return item.address1 + ', ' + item.city + ', ' + item.state + ' ' + item.zipCode;
      }
      if (dateFields.has(property)) {
        return item[property as 'lastReviewedDate' | 'expirationDate']
          ? new Date(item[property as 'lastReviewedDate' | 'expirationDate']).getTime()
          : 0;
      }
      const value = item[property as keyof ReportApplication];
      return typeof value === 'string' ? value.toLowerCase() : (value as number);
    };
  }
}
